import {useEffect, useState} from 'react';
import {useSelector} from 'react-redux';

import {useGetInboxStatus} from '@/api/iccNotificationApi';
import {ICC_INBOX_MESSAGE_STATUS_TYPES} from '@/consts/icc/iccInboxMessageStatusTypes';
import inboxMessageService from '@/services/inboxMessageService';
import notificationUIService from '@/services/pushNotification/notificationUIService';
import {makeSelectInboxMessage, makeSelectNextInboxMessage} from '@/state/selectors/inbox';

import useInboxMessages from './useInboxMessages';

const useNotificationDetails = ({messageId}) => {
    const message = useSelector(makeSelectInboxMessage(messageId));
    const nextMessage = useSelector(makeSelectNextInboxMessage(messageId));
    const {refetch: refetchInboxStatus} = useGetInboxStatus();
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [hasTriedRefresh, setHasTriedRefresh] = useState(false);

    useEffect(() => {
        const run = async () => {
            if (message?.status === ICC_INBOX_MESSAGE_STATUS_TYPES.UNREAD) {
                notificationUIService.removeNotification(messageId);
                await inboxMessageService.readInboxMessage(messageId);
                refetchInboxStatus();
            }
        };

        run();
    }, [message]);

    const {inboxMessages, loadMore, isLoading, hasNextPage} = useInboxMessages();

    // Reset refresh attempt when messageId changes
    useEffect(() => {
        setHasTriedRefresh(false);
    }, [messageId]);

    // Ensure the current message exists in the store when deep-linked (e.g., from push).
    useEffect(() => {
        if (!messageId) return;
        if (message) return;
        if (isRefreshing) return;
        if (hasTriedRefresh) return;

        const refreshFirstPage = async () => {
            setIsRefreshing(true);
            try {
                // Fetch the first page to include newly arrived messages.
                await inboxMessageService.fetchInboxMessages();
            } finally {
                setIsRefreshing(false);
                setHasTriedRefresh(true);
            }
        };

        refreshFirstPage();
    }, [messageId, message, isRefreshing, hasTriedRefresh]);

    useEffect(() => {
        if (!messageId) return;
        if (nextMessage) return;
        if (isLoading) return;

        if (hasNextPage || inboxMessages === undefined) {
            loadMore();
        }
    }, [hasNextPage, nextMessage, isLoading, messageId]);

    // Keep loader active until the current message is available,
    // but avoid infinite loading when message doesn't exist after a refresh attempt
    const isLoaderActive = isLoading || isRefreshing || (!message && !hasTriedRefresh);
    const notFound = hasTriedRefresh && !message;

    return {message, nextMessage, isLoaderActive, notFound};
};

export default useNotificationDetails;
